/**
 * #559 runtime AST 審査が 400 で返したときに、共通の EventDialog (`magician`
 * speaker) でユーザーに案内する composable。
 *
 * Backend が `ApiResponse(status_code=400, body="コード審査NG: <reason>")`
 * を返す。$fetch FetchError の `err.data` が body 文字列なので、そこから
 * prefix を剥がして reason を取り出し、`dialogStore.event` に流す。
 *
 * カスタムダイアログを増やさず、既存の `magician` speaker / fantasy-dialog
 * 世界観に乗せる方針（#559 レビュー反映）。
 *
 * 使い方:
 *
 *   const { tryShow } = useScreeningError()
 *   try { await api.createProjectSession(...) }
 *   catch (e) {
 *     if (tryShow(e)) return  // EventDialog で表示済み
 *     throw e                 // 別系統のエラーは呼び出し側で扱う
 *   }
 */

import type { DialogStep, SoundId, SpeakerId } from '~/types/interfaces'

const SCREENING_PREFIX = 'コード審査NG'

const extractReason = (err: unknown): string | null => {
  if (typeof err !== 'object' || err === null) return null
  const data = (err as { data?: unknown }).data
  // FastAPI adapter / Lambda+API Gateway 両方とも `ApiResponse.body` 文字列が
  // HTTP body。`.message` は server-side 専用（Discord 通知）で client には届かない。
  if (typeof data === 'string' && data.startsWith(SCREENING_PREFIX)) {
    return data.replace(/^コード審査NG[：:\s]*/, '')
  }
  // backend が将来 JSON object で返した場合の forward compat。
  if (typeof data === 'object' && data !== null) {
    const candidates = [
      (data as { body?: unknown }).body,
      (data as { message?: unknown }).message,
    ]
    for (const cand of candidates) {
      if (typeof cand === 'string' && cand.startsWith(SCREENING_PREFIX)) {
        return cand.replace(/^コード審査NG[：:\s]*/, '')
      }
    }
  }
  return null
}

/** "L3: ..." → "3行目: ..." のような行番号表記に書き換える。 */
const humanizeReason = (reason: string): string => reason.replace(/^L(\d+):/, '$1行目:')

const buildDialogStep = (reason: string): DialogStep => {
  // セリフ → 改行 → 補足 → 改行 → エラーメッセージ (コードブロック)。
  //
  // MDC が markdown を解釈する。
  // - 行末の 2 個の半角スペースが <br>（改行）に変換される（CommonMark 仕様）
  // - そのあと `<br>\n` を入れて空行（行間）を作る
  //
  // 行末スペースを保ちたいので、各行を 1 つの文字列リテラルにせず ` ' + \n'`
  // を明示的に書く（リンター・フォーマッタが行末空白を剥がすのを防ぐ）。
  const SP = '  ' // trailing 2 spaces = markdown hard line break
  const text
    = 'エンコーディアでは使えないコードが含まれているようね。' + SP + '\n'
      + '<br>\n'
      + '詳しいことは[エンコーディア通信](/articles/publish-code-rules)を読んでね。' + SP + '\n'
      + '<br>\n'
      + '```\n'
      + humanizeReason(reason)
      + '\n```'

  return {
    type: 'dialog',
    speaker: 'magician' as SpeakerId,
    title: '魔法ルールチェック',
    text,
    actions: {
      cancel: {
        label: 'とじる',
        disabled: false,
        sound: 'cancel' as SoundId,
      },
    },
  }
}

export function useScreeningError() {
  const dialogStore = useDialogStore()

  /**
   * AST 違反エラーなら EventDialog を開いて true を返す。違反でなければ false。
   * 呼び出し側は false の場合だけ通常のエラー処理（snackbar / rethrow）に進める。
   */
  const tryShow = (err: unknown): boolean => {
    const reason = extractReason(err)
    if (reason === null) return false
    dialogStore.showDialog('event', { params: buildDialogStep(reason) })
    return true
  }

  return { tryShow }
}
